import type { Coordinate } from '@orgsim/shared';
import { type ReactNode, useEffect, useState } from 'react';
import {
  CircleMarker,
  LayersControl,
  MapContainer,
  TileLayer,
  ZoomControl,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { MarkerLayout } from './MarkerLayout';
import { fromLatLng, gtaCrs, maxNativeZoom, reliefUrl, toLatLng } from './crs';
import { tileSets } from './tileSets';

type Props = {
  center?: Coordinate;
  zoom?: number;
  onClick?: (position: Coordinate) => void;
  children?: ReactNode;
  location?: Coordinate;
  focus?: { position: Coordinate; zoom?: number } | null;
};

function ClickCatcher({ onClick }: { onClick?: (position: Coordinate) => void }) {
  const map = useMap();
  // Keyboard events (Esc, Enter in DrawTools) only reach the map when its container has focus.
  useEffect(() => {
    map.getContainer().tabIndex = 0;
  }, [map]);
  useMapEvents({
    click(e) {
      onClick?.(fromLatLng(e.latlng));
    },
  });
  return null;
}

function MapFocus({ focus }: { focus: Props['focus'] }) {
  const map = useMap();
  useEffect(() => {
    if (focus) map.setView(toLatLng(focus.position), focus.zoom ?? map.getZoom());
  }, [focus, map]);
  useEffect(() => {
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  return null;
}

// Far enough in to set signs a few metres apart, although the atlas tiles stop at their native
// zoom and are only upscaled beyond it.
const MAX_ZOOM = 10;
// tools/relief renders the relief up to zoom 6. The heightmap behind it has cells of roughly
// 50 m; one level beyond that the layer is still readable, further in it switches itself off.
const RELIEF_NATIVE_ZOOM = 6;
const RELIEF_MAX_ZOOM = 7;

export function MapView({
  center = { x: 0, y: 0 },
  zoom = 4,
  onClick,
  children,
  location,
  focus,
}: Props) {
  const relief = reliefUrl();
  const sets = tileSets();
  const native = maxNativeZoom();
  const [spread, setSpread] = useState(true);
  return (
    <div className="relative h-full w-full">
      <MapContainer
        crs={gtaCrs}
        center={toLatLng(center)}
        zoom={zoom}
        minZoom={0}
        maxZoom={MAX_ZOOM}
        zoomControl={false}
        attributionControl={false}
        className="h-full w-full"
      >
        <LayersControl position="topright">
          {sets.map((set, i) => (
            <LayersControl.BaseLayer key={set.id} name={set.label} checked={i === 0}>
              <TileLayer
                url={set.url}
                tileSize={256}
                maxNativeZoom={native}
                maxZoom={MAX_ZOOM}
                noWrap
              />
            </LayersControl.BaseLayer>
          ))}
          {relief ? (
            <LayersControl.Overlay name="Relief">
              <TileLayer
                url={relief}
                tileSize={256}
                maxNativeZoom={RELIEF_NATIVE_ZOOM}
                maxZoom={RELIEF_MAX_ZOOM}
                opacity={0.8}
                noWrap
              />
            </LayersControl.Overlay>
          ) : null}
        </LayersControl>
        <ClickCatcher onClick={onClick} />
        <MapFocus focus={focus} />
        <ZoomControl position="bottomright" zoomInTitle="Vergrößern" zoomOutTitle="Verkleinern" />
        {location ? (
          <CircleMarker
            center={toLatLng(location)}
            radius={22}
            interactive={false}
            pathOptions={{ color: '#eae7e2', weight: 1.5, dashArray: '4 5', fillOpacity: 0 }}
          />
        ) : null}
        <MarkerLayout enabled={spread}>{children}</MarkerLayout>
      </MapContainer>
      <button
        type="button"
        className="map-spread-toggle"
        aria-pressed={spread}
        onClick={() => setSpread((v) => !v)}
        title="Eng stehende Objekte getrennt anzeigen. Linien zeigen ihre tatsächliche Position."
      >
        {spread ? 'Zeichen entzerrt' : 'Exakte Positionen'}
      </button>
      {sets.length === 0 ? (
        <output className="map-unavailable">
          Keine Hintergrundkarte eingerichtet. Objekte lassen sich trotzdem platzieren.
        </output>
      ) : null}
    </div>
  );
}
